import { useCallback, useEffect, useMemo, useState } from 'react';

import axios, { type AxiosRequestConfig } from 'axios';
import dayjs, { type Dayjs } from 'dayjs';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer, useContainer } from 'unstated-next';

import {
    filterByEndDate,
    filterByInstructor,
    filterBySearch,
    filterByStartDate,
    filterByCreatedAfterDate,
    sortByClassName,
    sortByDates,
    sortByInstructors,
    sortByCreated
} from './ClassFilters/filters';
import {
    type ClassWithInstructors,
    type Direction,
    type InstructorWithEmail,
    type Option,
    type SortBy,
    SortOption,
    TimeFilter,
    type ColumnVisibility,
    ArchiveAction
} from './types';
import { CLASSES, CLASSES_BATCH_UPDATE } from '../../../../config/apiPaths';
import { ClassStatus } from '../../../../context/types';
import { Status } from '../../../../types/apiTypes';
import { getUrls } from '../../../../utils/url';
import AdminAuthContainer from '../../AdminAuthContainer';

const useClasses = () => {
    const { t } = useTranslation('admin_settings');
    const { uuid } = useContainer(AdminAuthContainer);
    const [status, setStatus] = useState<Status>(Status.NotStarted);

    const [classes, setClasses] = useState<ClassWithInstructors[]>([]);
    const [instructors, setInstructors] = useState<InstructorWithEmail[]>([]);

    const [selectedInstructor, setSelectedInstructor] = useState<Option | null>(null);
    const [searchValue, setSearchValue] = useState('');

    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [timeFilter, setTimeFilter] = useState<TimeFilter>(TimeFilter.Current);

    const [sortDirection, setSortDirection] = useState<Direction>(undefined);
    const [sortBy, setSortBy] = useState<SortBy>(undefined);

    const [selectedClass, setSelectedClass] = useState<ClassWithInstructors | null>(null);
    const [selectedDownloadTranscriptsClass, setSelectedDownloadTranscriptsClass] =
        useState<ClassWithInstructors | null>(null);

    // Column visibility state
    const [useCreatedDateFilter, setUseCreatedDateFilter] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>({
        className: true,
        instructor: true,
        dates: !useCreatedDateFilter,
        created: useCreatedDateFilter
    });

    // Update column visibility when date filter type changes
    useEffect(() => {
        setColumnVisibility((prev) => ({
            ...prev,
            dates: !useCreatedDateFilter,
            created: useCreatedDateFilter
        }));
    }, [useCreatedDateFilter]);

    const toggleColumnVisibility = useCallback((column: keyof ColumnVisibility) => {
        setColumnVisibility((prev) => ({
            ...prev,
            [column]: !prev[column]
        }));
    }, []);

    const { enqueueSnackbar } = useSnackbar();

    const { api } = getUrls();

    const fetchClasses = useCallback(async () => {
        if (!uuid) return;

        try {
            setStatus(Status.InProgress);

            const config: AxiosRequestConfig = {
                headers: {
                    token: uuid
                },
                params: { filter: timeFilter }
            };

            const { data } = await axios.get<ClassWithInstructors[]>(`${api}${CLASSES}`, config);

            const instructorMap = {};

            data.forEach((classWithInstructors) => {
                classWithInstructors.instructors.forEach((instructor) => {
                    if (instructor.email) {
                        instructorMap[instructor.email.toLocaleLowerCase()] = instructor;
                    }
                });
            });

            setInstructors(Object.values(instructorMap));
            setClasses(data);
        } catch (err) {
            enqueueSnackbar(t('classes.somethingWentWrong'), { variant: 'error' });
        }

        setStatus(Status.Complete);
    }, [uuid, api, timeFilter, enqueueSnackbar, t]);

    useEffect(() => {
        fetchClasses();
    }, [fetchClasses]);

    const filteredClassesByInstructor: ClassWithInstructors[] = useMemo(
        () => filterByInstructor({ classes, selectedInstructor }),
        [classes, selectedInstructor]
    );
    const filteredClassesBySearch: ClassWithInstructors[] = useMemo(
        () => filterBySearch({ classes: filteredClassesByInstructor, searchValue }),
        [filteredClassesByInstructor, searchValue]
    );
    const filteredClassesByStartDate: ClassWithInstructors[] = useMemo(() => {
        if (useCreatedDateFilter) {
            // Use startDate as createdAfterDate when in creation date filter mode
            return filterByCreatedAfterDate({ classes: filteredClassesBySearch, createdAfterDate: startDate });
        } else {
            // Use startDate for class start date filtering
            return filterByStartDate({ classes: filteredClassesBySearch, startDate });
        }
    }, [filteredClassesBySearch, startDate, useCreatedDateFilter]);
    const filteredClassesByEndDate: ClassWithInstructors[] = useMemo(() => {
        if (useCreatedDateFilter) {
            // When using creation date filter, use endDate for creation date end range
            return filteredClassesByStartDate.filter((classValue) => {
                if (!endDate) return true;

                const classCreatedDateString = classValue.created_at;
                if (!classCreatedDateString) return false;

                const classCreatedDate = dayjs(classCreatedDateString);
                const isBeforeEndDate = endDate.isAfter(classCreatedDate.toISOString(), 'day');
                const isSameEndDate = endDate.isSame(classCreatedDate.toISOString(), 'day');

                return isBeforeEndDate || isSameEndDate;
            });
        } else {
            // Use endDate for class end date filtering
            return filterByEndDate({ classes: filteredClassesByStartDate, endDate });
        }
    }, [endDate, filteredClassesByStartDate, useCreatedDateFilter]);

    const clonedClasses = _.cloneDeep(filteredClassesByEndDate);
    const sortedClassesByName: ClassWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.NAME
                ? sortByClassName({ classes: clonedClasses, sort: sortDirection })
                : clonedClasses,
        [clonedClasses, sortBy, sortDirection]
    );
    const sortedClassesByDates: ClassWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.DATES
                ? sortByDates({ classes: sortedClassesByName, sort: sortDirection })
                : sortedClassesByName,
        [sortedClassesByName, sortBy, sortDirection]
    );
    const sortedClassesByCreated: ClassWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.CREATED
                ? sortByCreated({ classes: sortedClassesByDates, sort: sortDirection })
                : sortedClassesByDates,
        [sortedClassesByDates, sortBy, sortDirection]
    );
    const filteredClasses: ClassWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.INSTRUCTOR
                ? sortByInstructors({ classes: sortedClassesByCreated, sort: sortDirection })
                : sortedClassesByCreated,
        [sortedClassesByCreated, sortBy, sortDirection]
    );

    // Handle batch update of class status
    const batchUpdateClassStatus = useCallback(
        async (classUuids: string[], action: ArchiveAction) => {
            const { api } = getUrls();

            // Convert action to status
            const status = action === ArchiveAction.Archive ? ClassStatus.Archived : ClassStatus.Published;

            try {
                const config: AxiosRequestConfig = {
                    headers: {
                        token: uuid
                    }
                };

                const payload = {
                    user_uuid: uuid,
                    class_uuids: classUuids,
                    status
                };

                const { data } = await axios.patch(`${api}${CLASSES_BATCH_UPDATE}`, payload, config);

                // Refresh the classes list after successful update
                await fetchClasses();

                // Show appropriate success message based on action with count
                const successKey =
                    action === ArchiveAction.Archive ? 'successfully-archived' : 'successfully-unarchived';
                enqueueSnackbar(t(`classes.${successKey}`, { count: classUuids.length }), { variant: 'success' });

                return data;
            } catch (error) {
                console.error('Error updating class status:', error);

                // Show appropriate error message based on action with count
                const errorKey = action === ArchiveAction.Archive ? 'failed-to-archived' : 'failed-to-unarchived';
                enqueueSnackbar(t(`classes.${errorKey}`, { count: classUuids.length }), { variant: 'error' });
                throw error;
            }
        },
        [uuid, fetchClasses, enqueueSnackbar, t]
    );

    return {
        endDate,
        status,
        startDate,
        instructors,
        searchValue,
        setEndDate,
        setStartDate,
        setSearchValue,

        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,

        timeFilter,
        setTimeFilter,
        filteredClasses,
        selectedInstructor,
        setSelectedInstructor,

        classes,
        setClasses,
        fetchClasses,
        batchUpdateClassStatus,

        selectedClass,
        setSelectedClass,

        selectedDownloadTranscriptsClass,
        setSelectedDownloadTranscriptsClass,

        columnVisibility,
        toggleColumnVisibility,

        useCreatedDateFilter,
        setUseCreatedDateFilter,

        uuid
    };
};

const ClassesContainer = createContainer(useClasses);

export default ClassesContainer;
